import type { PortalNotification } from '../../modules/notifications/api'
import type { AccountProcessingStatus } from '@tenfour/contracts'

export interface SystemNotification {
  id: string
  title: string
  detail: string
  href?: string
  attention: boolean
  busy: boolean
}

/** Only unfinished or actionable setup belongs in the notification tray. */
export function systemNotifications(status?: AccountProcessingStatus): SystemNotification[] {
  const items: SystemNotification[] = (status?.tasks ?? []).map((task) => ({
    id: task.id,
    title: task.label,
    detail: task.state === 'failed' ? 'Needs attention' : task.state === 'retrying' ? 'Retrying' : task.state === 'queued' ? 'Queued' : 'In progress',
    href: task.href,
    attention: task.state === 'failed',
    busy: task.state !== 'failed',
  }))
  const transfer = status?.number?.transfer
  if (transfer && transfer.status !== 'COMPLETED') {
    const attention = ['REJECTED', 'CANCELED'].includes(transfer.status) || transfer.providerStatus === 'action_required'
    items.push({ id: `transfer:${transfer.id}`, title: 'Number transfer', detail: transfer.statusNote ?? (attention ? 'Your transfer needs attention.' : 'Waiting for your carrier.'), attention, busy: false, href: '/settings/phone-assistant' })
  }
  for (const calendar of status?.calendars ?? []) {
    const attention = calendar.status === 'ERROR' || Boolean(calendar.lastError)
    if (!attention && calendar.lastSyncedAt) continue
    items.push({ id: `calendar:${calendar.id}`, title: 'Google Calendar', detail: attention ? 'Sync needs attention. Open Calendar to review.' : 'Waiting for the first sync.', href: '/calendar', attention, busy: false })
  }
  return items
}

/** Reading a destination does not resolve a Needs your attention handoff or mark an item read. */
export function notificationHref(item: PortalNotification): string {
  if (item.handoff) return item.handoff.status === 'OPEN' && !item.read
    ? `/queue?item=${encodeURIComponent(`handoff:${item.handoff.id}`)}`
    : `/aria/${encodeURIComponent(item.handoff.links.ariaThreadId)}`
  if (!item.entityId) return '/notifications'
  const id = encodeURIComponent(item.entityId)
  if (item.entityType === 'invoice') return `/invoices/${id}`
  if (item.entityType === 'job') return `/jobs/${id}`
  if (item.entityType === 'conversation') return `/conversations/${id}`
  return '/notifications'
}
